import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-quartz.css";
import { useMemo, useState, useEffect } from "react";
import { useSelector } from "react-redux";
import theme from "../config/theme";
import { ColDef } from "ag-grid-community";
import ReleaseButton from "./publish-toggle";
import { AgGridReact } from "ag-grid-react";
import DeleteButton from "./delete-toggle";
import EditButton from "./edit-toggle";
import Modal from "./basicComponents/modal";

export interface Column<T> extends ColDef {
  headerName: string;
  field?: Extract<keyof T, string>;
  editable?: (params: any) => boolean;
  valueGetter?: (params: any) => string;
}

type AutoSizeStrategy = {
  type: "fitGridWidth";
  defaultMinWidth: number;
  columnLimits: { colId: string; minWidth: number }[];
};

interface UserGridProps<T> {
  columns: Column<T>[];
  btnHeader?: string;
  editBtn?: boolean;
  deleteBtn?: boolean;
  publishBtn?: boolean;
  userData: T[];
  pageSize: number;
  editable: boolean;
  pagination?: boolean;
  height?: number;
  handleDelete?: (id: string) => Promise<void>;
  handleEdit?: (id?: any, data?: any) => Promise<void>;
  handleSetData?: (clear: boolean, id?: any) => Promise<void>;
  onCellValueChanged?: (params: any) => void;
}

const Grid = <T,>({
  columns,
  btnHeader,
  editBtn,
  deleteBtn,
  publishBtn,
  userData,
  pageSize,
  pagination,
  editable,
  height,
  handleDelete,
  handleEdit,
  handleSetData,
  onCellValueChanged,
}: UserGridProps<T>) => {
  const { theme: themeMode } = useSelector((state: any) => state.globalState);
  const mode = themeMode === "light" ? theme.light : theme.dark;
  const [gridData, setGridData] = useState<T[]>(userData);
  const [editingRowId, setEditingRowId] = useState<string | null>(null);

  useEffect(() => {
    setGridData(userData);
  }, [userData]);

  const operationButtons = (params: any) => {
    const rowId = params.data.id ? params.data.id : params.data._id;
    return (
      <div className="flex items-center">
        {editBtn ? (
          <EditButton
            id={rowId}
            isEditing={editingRowId === rowId}
            setEditingRowId={setEditingRowId}
            editable={editable}
            handleEdit={
              handleEdit ? () => handleEdit(rowId, gridData) : async () => {}
            }
            handleSetData={
              handleSetData
                ? (clear: boolean, id: any) => handleSetData(clear, id)
                : async () => {}
            }
          />
        ) : null}
        {deleteBtn && handleDelete ? (
          <DeleteButton
            id={params.data._id}
            handleDelete={() => handleDelete(rowId)}
          />
        ) : null}
        {publishBtn ? <ReleaseButton rowId={rowId} /> : null}
      </div>
    );
  };

  const defaultColDef = useMemo(() => {
    return {
      resizable: false,
      cellStyle: () => ({
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        fontFamily: `${mode.fontFamily.normal}`,
        fontSize: `${mode.fontSize.h4}`,
        flex: 2,
      }),
    };
  }, []);

  const autoSizeStrategy: AutoSizeStrategy = {
    type: "fitGridWidth",
    defaultMinWidth: 100,
    columnLimits: [{ colId: "country", minWidth: 900 }],
  };

  const columnDefs: ColDef<T>[] = columns.map((column: any) => ({
    ...column,
    editable:
      editable &&
      ((params: any) =>
        params.data ? params.data._id === editingRowId : false),
  }));

  if (btnHeader) {
    columnDefs.push({
      headerName: btnHeader,
      cellRenderer: operationButtons,
    });
  }

  return (
    <>
      <Modal />
      <div
        className="ag-theme-quartz"
        style={{ height: height ? height : "350px", width: "100%" }}
      >
        <style>
          {`
            .ag-theme-quartz {
            --ag-foreground-color: ${mode.color.textPrimary};
            --ag-background-color: ${mode.backgroundColor.bgSecondary};
            --ag-header-foreground-color: ${mode.color.textBlueWhite};
            --ag-header-background-color: ${mode.backgroundColor.bgSecondary};
            --ag-header-column-resize-handle-color: ${mode.color.textPrimary};
            --ag-row-border-color: ${mode.color.textPrimary};
            --ag-font-size:12px;
            --ag-font-weight :bold;
            --ag-cell-horizontal-padding:30px;
            --ag-row-height:50px;
            --ag-borders:none;
          }


      .ag-body-horizontal-scroll{
            padding:-25px !important;
      height: 20px !important;
       max-height: 9px !important;
        min-height: 4px !important;
        }
      
      .ag-body-horizontal-scroll-viewport {
      padding:-15px !important;
        height: 8px !important;
        max-height: 18px !important;
        min-height: 4px !important;
      }

          
          .ag-header-cell-label {
          justify-content: center;
          }

          .ag-theme-quartz .ag-header {
            font-family: ${mode.fontFamily.normal};
            font-weight: 0.8;
            font-size: 13px;
          }

          .ag-root-wrapper {
            position: relative !important;
          }

          .ag-root-wrapper-body {
            margin-bottom: 50px;
          }

          .ag-paging-panel {
       position: absolute !important;
    bottom: 0 !important;
    /* right: 25% !important; */
    border-top: none !important;
    width: 100%!important;
    display: flex!important
;
    flex-wrap: wrap!important;
    justify-content: center!important;


          }
            .ag-cell-hidden {
      display: none;
    }
          `}
        </style>
        <div style={{ width: "100%", height: "100%" }}>
          <AgGridReact
            autoSizeStrategy={autoSizeStrategy}
            rowData={gridData}
            columnDefs={columnDefs}
            domLayout="normal"
            suppressColumnVirtualisation={true}
            pagination={pagination}
            paginationPageSize={pageSize}
            paginationPageSizeSelector={[
              pageSize,
              pageSize * 1.5,
              pageSize * 2,
            ]}
            enableRtl={true}
            defaultColDef={defaultColDef}
            onCellValueChanged={onCellValueChanged}
          />
        </div>
      </div>
    </>
  );
};

export default Grid;
